import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectors } from "reducers/patient-outreaches";
import { selectors as rootSelectors } from "reducers";
import {
  fetchPatientOutreachList,
  setSort
} from "actions/patient-outreaches";
import useRowSelectNew, {
  SelectRowContext,
  RowSelectCell,
  RowSelectHeaderCell
} from "hooks/useRowSelectNew";
import OutreachStateBadge from "./outreach-state-badge";
import PaginationFooter from "./pagination-footer";
import RowEligibilityButton from "./row-eligibility-button";
import RowActionMenu from "./row-action-menu";
import BulkActionsToolbar from "./bulk-actions-toolbar";

const COLUMNS = [
  { key: "_eligibility", label: "", sortable: false },
  { key: "_actions", label: "", sortable: false },
  { key: "patientAccountNumber", label: "Acct #", sortable: true },
  { key: "patientName", label: "Name", sortable: true },
  { key: "patientPhone", label: "Phone", sortable: false },
  { key: "patientEmail", label: "Email", sortable: false },
  { key: "allocatedCoachName", label: "Allocated to", sortable: true },
  { key: "teamName", label: "Team", sortable: true },
  { key: "companyName", label: "Company", sortable: true },
  { key: "outreachState", label: "State", sortable: true },
  { key: "lastContactAttempt", label: "Last contact", sortable: true },
  { key: "dtAllocated", label: "Date allocated", sortable: true },
  { key: "cycleCount", label: "Cycle", sortable: true },
  { key: "productLines", label: "Product lines", sortable: false },
  { key: "tcpa", label: "Call window", sortable: false }
];

const formatDate = v => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString();
};

const metaExtractor = row => ({
  patientGuid: row.patientGuid,
  patientName: row.patientName,
  patientAccountNumber: row.patientAccountNumber
});

const Grid = () => {
  const dispatch = useDispatch();
  const rows = useSelector(selectors.getRows);
  const filters = useSelector(selectors.getFilters);
  const page = useSelector(selectors.getPage);
  const pageSize = useSelector(selectors.getPageSize);
  const sort = useSelector(selectors.getSort);
  const isFetching = useSelector(selectors.getIsFetchingList);
  const userRole = useSelector(rootSelectors.getUserRole);
  const isAdmin = userRole === "Administrator";

  // useRowSelectNew clears on query.filters / query.sort ref change.
  // Bundle page into filters so paging also clears (v1 supports per-page
  // selection only — see plan "What We're NOT Doing" section).
  const selectQuery = useMemo(
    () => ({ filters: { ...filters, _page: page }, sort }),
    [filters, page, sort]
  );

  const {
    selectedRows,
    isRowSelected,
    toggleRow,
    toggleAllRows,
    clearSelectedRows
  } = useRowSelectNew(rows, "patientGuid", selectQuery, metaExtractor);

  useEffect(() => {
    dispatch(
      fetchPatientOutreachList({
        filters,
        page,
        pageSize,
        sortBy: sort.sortBy,
        sortDir: sort.sortDir
      })
    );
  }, [dispatch, filters, page, pageSize, sort.sortBy, sort.sortDir]);

  const handleSort = col => {
    if (!col.sortable) return;
    if (sort.sortBy === col.key) {
      dispatch(
        setSort({
          sortBy: col.key,
          sortDir: sort.sortDir === "ASC" ? "DESC" : "ASC"
        })
      );
    } else {
      dispatch(setSort({ sortBy: col.key, sortDir: "ASC" }));
    }
  };

  const sortIndicator = col => {
    if (!col.sortable || sort.sortBy !== col.key) return null;
    return sort.sortDir === "ASC" ? " ▲" : " ▼";
  };

  const totalColCount = COLUMNS.length + (isAdmin ? 1 : 0);
  const selectContext = useMemo(
    () => ({ isRowSelected, toggleRow, toggleAllRows }),
    [isRowSelected, toggleRow, toggleAllRows]
  );

  return (
    <SelectRowContext.Provider value={selectContext}>
      <div className="patient-outreach-grid">
        {isAdmin && (
          <BulkActionsToolbar
            selectedRows={selectedRows}
            clearSelection={clearSelectedRows}
          />
        )}
        <div className="patient-outreach-grid__table-wrap">
          <table className="patient-outreach-grid__table">
            <thead>
              <tr>
                {isAdmin && <RowSelectHeaderCell />}
                {COLUMNS.map(col => (
                  <th
                    key={col.key}
                    className={col.sortable ? "sortable" : ""}
                    onClick={() => handleSort(col)}
                  >
                    {col.label}
                    {sortIndicator(col)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isFetching && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={totalColCount}
                    className="patient-outreach-grid__loading"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!isFetching && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={totalColCount}
                    className="patient-outreach-grid__empty"
                  >
                    No patients match the current filters.
                  </td>
                </tr>
              )}
              {rows.map(row => (
                <tr key={row.patientGuid}>
                  {isAdmin && <RowSelectCell id={row.patientGuid} />}
                  <td className="patient-outreach-grid__row-action">
                    <RowEligibilityButton patientGuid={row.patientGuid} />
                  </td>
                  <td className="patient-outreach-grid__row-action">
                    <RowActionMenu row={row} />
                  </td>
                  <td>{row.patientAccountNumber || "—"}</td>
                  <td>
                    {row.patientName ? (
                      <Link to={`/patients/${row.patientGuid}`}>
                        {row.patientName}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>{row.patientPhone || "—"}</td>
                  <td>{row.patientEmail || "—"}</td>
                  <td>{row.allocatedCoachName || "—"}</td>
                  <td>{row.teamName || "—"}</td>
                  <td>{row.companyName || "—"}</td>
                  <td>
                    <OutreachStateBadge
                      outreachState={row.outreachState}
                      isSynthpopWaitingOnCallback={
                        row.isSynthpopWaitingOnCallback
                      }
                    />
                  </td>
                  <td>{formatDate(row.lastContactAttempt)}</td>
                  <td>{formatDate(row.dtAllocated)}</td>
                  <td>{row.cycleCount ?? "—"}</td>
                  <td>{row.productLines || "—"}</td>
                  <td>
                    {row.tcpaStart && row.tcpaEnd
                      ? `${row.tcpaStart}–${row.tcpaEnd}`
                      : "Call window not configured"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationFooter />
      </div>
    </SelectRowContext.Provider>
  );
};

export default Grid;
